import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Box,
    Typography,
    Avatar
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const OptionValuesTable = ({
    values,
    onEdit,
    onDelete,
    displayType
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');


    return (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
                <TableHead sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                    <TableRow>
                        <TableCell width={60} align="center" sx={{ fontWeight: 'bold' }}>#</TableCell>
                        <TableCell align={isArabic ? 'right' : 'left'} sx={{ fontWeight: 'bold' }}>
                            {isArabic ? 'الاسم' : 'Name'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {displayType === 'color'
                                ? (isArabic ? 'اللون' : 'Color')
                                : displayType === 'image'
                                    ? (isArabic ? 'الصورة' : 'Image')
                                    : (isArabic ? 'القيمة' : 'Value')}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isArabic ? 'الحالة' : 'Status'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isArabic ? 'الإجراءات' : 'Actions'}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {values?.length > 0 ? (
                        values.map((item, index) => (
                            <TableRow key={item.id} hover>
                                <TableCell align="center">{index + 1}</TableCell>

                                {/* Name */}
                                <TableCell align={isArabic ? 'right' : 'left'}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {item.name?.[lng] || item.name?.en || '---'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {lng === 'ar' ? item.name?.en : item.name?.ar}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                {/* Value */}
                                <TableCell align="center">
                                    {displayType === 'color' ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '6px',
                                                    backgroundColor: item.value,
                                                    border: '2px solid',
                                                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    transition: 'transform 0.2s',
                                                    '&:hover': { transform: 'scale(1.1)' }
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                                                    fontWeight: 'bold',
                                                    color: 'text.secondary',
                                                    letterSpacing: 0.5
                                                }}
                                            >
                                                {item.value?.toUpperCase()}
                                            </Typography>
                                        </Box>
                                    ) : displayType === 'image' ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <Avatar
                                                src={item.image?.preview_url || item.image?.url || item.image?.thumb_url}
                                                alt={item.name?.[lng]}
                                                variant="rounded"
                                                sx={{
                                                    width: 45,
                                                    height: 45,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: 'background.paper',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    '& img': { objectFit: 'cover' }
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Chip
                                            label={item.value}
                                            size="small"
                                            variant="soft"
                                            sx={{
                                                fontWeight: 600,
                                                borderRadius: '6px',
                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                border: 'none'
                                            }}
                                        />
                                    )}
                                </TableCell>

                                {/* Status */}
                                <TableCell align="center">
                                    <Chip
                                        label={item.is_active ? (isArabic ? 'نشط' : 'Active') : (isArabic ? 'غير نشط' : 'Inactive')}
                                        color={item.is_active ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>

                                {/* Actions */}
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <IconButton size="small" color="primary" onClick={() => onEdit(item)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => onDelete(item.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="textSecondary">
                                    {isArabic ? 'لا توجد قيم مضافة بعد' : 'No values added yet'}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OptionValuesTable;
